import React, { useEffect, useState } from "react";
import "react-responsive-modal/styles.css";
import { Modal } from "react-responsive-modal";
import { useTranslation } from "react-i18next";
import { API_BASE } from "../../config/api";

const isNoticeCurrentlyVisible = (notice, now = new Date()) => {
  if (!notice?.isActive) return false;

  const validFrom = notice.validFrom ? new Date(notice.validFrom) : null;
  const validTo = notice.validTo ? new Date(notice.validTo) : null;

  if (validFrom && !Number.isNaN(validFrom.getTime()) && validFrom > now) {
    return false;
  }

  if (validTo && !Number.isNaN(validTo.getTime()) && validTo < now) {
    return false;
  }

  return true;
};

const sanitizeHtml = (value) => {
  if (!value) return "";
  if (typeof window === "undefined") return String(value);

  const doc = new DOMParser().parseFromString(String(value), "text/html");
  doc.querySelectorAll("script, style").forEach((node) => node.remove());

  doc.body.querySelectorAll("*").forEach((node) => {
    [...node.attributes].forEach((attr) => {
      const name = attr.name.toLowerCase();
      const attrValue = String(attr.value || "").trim().toLowerCase();

      if (name.startsWith("on")) {
        node.removeAttribute(attr.name);
      }

      if (name === "href" && attrValue) {
        try {
          const parsed = new URL(attrValue, window.location.origin);
          if (parsed.protocol.replace(":", "") === "javascript") {
            node.removeAttribute(attr.name);
          }
        } catch (_error) {
          // Ignore invalid URLs.
        }
      }
    });
  });

  return doc.body.innerHTML;
};

const NoticeModal = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [notices, setNotices] = useState([]);

  useEffect(() => {
    let ignore = false;

    const loadActiveNotices = async () => {
      try {
        const response = await fetch(`${API_BASE}/notices`);
        const data = await response.json().catch(() => []);

        if (!response.ok || ignore) {
          return;
        }

        const visibleNotices = (Array.isArray(data) ? data : []).filter(
          (notice) => isNoticeCurrentlyVisible(notice),
        );
        setNotices(visibleNotices);
        setOpen(visibleNotices.length > 0);
      } catch (_error) {
        if (!ignore) {
          setNotices([]);
          setOpen(false);
        }
      }
    };

    loadActiveNotices();

    return () => {
      ignore = true;
    };
  }, []);

  useEffect(() => {
    const handleOpenNoticeModal = () => {
      if (notices.length > 0) {
        setOpen(true);
      }
    };

    window.addEventListener("open-notice-modal", handleOpenNoticeModal);
    return () => {
      window.removeEventListener("open-notice-modal", handleOpenNoticeModal);
    };
  }, [notices.length]);

  if (!notices.length) {
    return null;
  }

  return (
    <Modal
      open={open}
      onClose={() => setOpen(false)}
      center
      focusTrapped={false}
      classNames={{
        overlay: "prescription-modal-overlay",
        modal: "prescription-modal",
      }}
    >
      <div className="prescription-modal-scroll">
        <div className="prescription-modal-header">
          <div className="prescription-modal-icon">
            <i className="fi flaticon-medical-report"></i>
          </div>
          <p className="prescription-modal-kicker">
            {t("notice_modal_kicker")}
          </p>
        </div>

        <div className="prescription-modal-content">
          <h2>{t("notice_modal_title")}</h2>
          <p>{t("notice_modal_subtitle")}</p>

          <div style={{ display: "grid", gap: 16, marginTop: 24 }}>
            {notices.map((notice) => (
              <div
                key={notice.id}
                style={{
                  border: "1px solid rgba(31, 120, 200, 0.14)",
                  borderRadius: 16,
                  padding: 18,
                  background: "rgba(248, 252, 255, 0.9)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 12,
                    alignItems: "flex-start",
                  }}
                >
                  <h3 style={{ margin: 0, color: "#0d1b5e", fontWeight: 700 }}>
                    {notice.title}
                  </h3>
                  {notice.showTime && (
                    <span
                      style={{
                        color: "#1f78c8",
                        fontWeight: 700,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {notice.showTime.slice(0, 5)}
                    </span>
                  )}
                </div>
                <div
                  style={{
                    margin: "12px 0 0",
                    lineHeight: 1.7,
                    color: "rgba(23, 50, 77, 0.85)",
                  }}
                  dangerouslySetInnerHTML={{
                    __html: sanitizeHtml(notice.description),
                  }}
                />
              </div>
            ))}
          </div>
        </div>

        <div className="prescription-modal-actions" style={{ marginTop: 32 }}>
          <button className="theme-btn" onClick={() => setOpen(false)}>
            {t("notice_modal_close")}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default NoticeModal;
